import { createSelector, Selector } from '@reduxjs/toolkit'
import { EntityType, Market, MarketDetails, MarketStatus, Platform, PlatformDetail, PlatformFeature } from 'api'
import { GlobalState } from 'store'
import { createAllEntityUpdatesSelector } from './entitySelectors'
import { DateLike, ParametricSelector, parseDateLike } from 'utils'
import { min } from 'date-fns'

export const platformDetailSelector: Selector<GlobalState, PlatformDetail | null> = createSelector(
  createAllEntityUpdatesSelector(EntityType.PLATFORM),
  platformUpdates => {
    if (platformUpdates.length === 0) {
      return null
    } else if (platformUpdates.length !== 1) {
      throw new Error('Found multiple platform entries')
    } else {
      const update = platformUpdates[0]
      return {
        platform: update.entity,
        availableEvents: update.availableEvents
      }
    }
  }
)

export const platformTradeDateSelector: Selector<GlobalState, DateLike | null> = createSelector(
  platformDetailSelector,
  platformDetail => platformDetail?.platform.marketDetails[Market.EUROPE]?.tradeDate ?? null
)

export const createMarketDetailSelector = <TProps>(
  marketSelector: ParametricSelector<{}, TProps, string | null | undefined>
): Selector<GlobalState, MarketDetails | undefined> =>
  createSelector(marketSelector, platformDetailSelector, (market, platformDetail) => {
    return market === Market.US ? platformDetail?.platform.marketDetails[Market.US] : platformDetail?.platform.marketDetails[Market.EUROPE]
  })

export const marketDetailsSelector: Selector<GlobalState, Platform['marketDetails'] | undefined> = createSelector(
  platformDetailSelector,
  platformDetail => platformDetail?.platform?.marketDetails
)

export const platformPreMaturityDaysSelector: Selector<GlobalState, number | null> = createSelector(
  platformDetailSelector,
  platformDetail => platformDetail?.platform.preMaturityDays ?? null
)

export const createPlatformFeatureEnabledSelector = (platformFeature: PlatformFeature): Selector<GlobalState, boolean> =>
  createSelector([platformDetailSelector], platformDetail => !!platformDetail?.platform.enabledFeatures.includes(platformFeature))

export const createPlatformMarketStatusClosedSelector = () =>
  createSelector(
    platformDetailSelector,
    platformDetail =>
      platformDetail?.platform.marketDetails[Market.EUROPE]?.marketStatus === MarketStatus.MARKET_CLOSED &&
      platformDetail?.platform.marketDetails[Market.US]?.marketStatus === MarketStatus.MARKET_CLOSED
  )

export const marketDetailGetterSelector: Selector<GlobalState, (market: Market | undefined) => MarketDetails | null> = createSelector(
  marketDetailsSelector,
  markets => {
    return function getMarketDetail(market: Market | undefined) {
      const marketDetail = market && markets?.[market]
      return marketDetail ?? markets?.EUROPE ?? null
    }
  }
)

export const minimumMarketTradeDateSelector: Selector<GlobalState, Date> = createSelector(marketDetailsSelector, marketDetails => {
  const euTradeDate = marketDetails?.EUROPE?.tradeDate
  const usTradeDate = marketDetails?.US?.tradeDate

  if (euTradeDate && usTradeDate) {
    return min([parseDateLike(euTradeDate), parseDateLike(usTradeDate)])
  } else {
    return new Date()
  }
})
